/**
 * Local-timezone date utilities — Phase 2 Group A.
 *
 * Goal: every "today" / "this week" / display-of-server-timestamp calculation
 * happens in the BROWSER'S wall-clock timezone, not UTC. The app serves users
 * in Saskatchewan (UTC-6, no DST) and India (UTC+5:30); both must see their
 * own local day on the Dashboard and Deadline Calendar.
 *
 * Server timestamps remain UTC ISO strings on the wire — these helpers parse
 * them as UTC (which they are) and format via `toLocaleString` in the user's
 * timezone. The bug we are fixing is computing "today" CLIENT-SIDE via
 * `new Date().toISOString().slice(0,10)` (which is UTC, not local), NOT in
 * how we display server timestamps.
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Returns a Date pinned to the user's current wall-clock moment.
 * Do NOT anchor to UTC midnight. Equivalent to `new Date()` but named
 * explicitly so call sites communicate intent.
 *
 * Verified correct in both America/Regina (UTC-6) and Asia/Kolkata (UTC+5:30):
 * `getTodayLocal().getDate()` returns the date number on the user's
 * physical wall clock at the moment of call.
 */
export function getTodayLocal() {
  return new Date();
}

/**
 * Returns YYYY-MM-DD for the given Date in the BROWSER'S local timezone.
 * Use anywhere you need to compare against a task's `due_date` (which is
 * stored as a YYYY-MM-DD string) without UTC drift.
 *
 * Bug this avoids: `new Date().toISOString().slice(0, 10)` returns the UTC
 * date. At 23:30 local in Saskatchewan that's already "tomorrow" in UTC,
 * so the "today" comparison skews by ±1 day.
 */
export function getLocalDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Display a date or Date-like value in the user's local timezone.
 * Thin wrapper around `Date.prototype.toLocaleDateString` that handles
 * date-only YYYY-MM-DD strings (interpreted as local midnight, not UTC
 * midnight) so they don't drift across the date boundary.
 */
export function formatLocalDate(value, options) {
  if (!value) return '';
  try {
    const d = _coerceDate(value);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, options);
  } catch {
    return '';
  }
}

/**
 * Display a datetime in the user's local timezone.
 * Server timestamps come in as UTC ISO strings ("…T…Z" / "…+00:00") and
 * `new Date()` parses them correctly. `toLocaleString` then renders the
 * wall-clock value the user expects.
 */
export function formatLocalDateTime(value, options) {
  if (!value) return '';
  try {
    const d = _coerceDate(value);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, options);
  } catch {
    return '';
  }
}

/**
 * Header-style: "Tuesday, May 19, 2026" — full weekday + full month, no
 * ordinal, always in local timezone.
 */
export function formatLocalDateHeader(value = new Date()) {
  return formatLocalDate(value, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Given an ISO string OR a YYYY-MM-DD string, return the LOCAL-TZ
 * YYYY-MM-DD. Required because `task.due_date` is sometimes a date-only
 * string and sometimes a full ISO timestamp — and `iso.slice(0,10)` gives
 * the UTC date for full ISOs, which is wrong for east/west of UTC.
 */
export function isoToLocalDateString(value) {
  if (!value) return '';
  const s = String(value);
  if (s.length === 10) return s;            // already date-only, local
  const d = _coerceDate(s);
  if (!d || isNaN(d.getTime())) return s.slice(0, 10);
  return getLocalDateString(d);
}

// Internal — parse a YYYY-MM-DD string as LOCAL midnight (not UTC),
// and any other ISO string via native Date.
function _coerceDate(value) {
  if (value instanceof Date) return value;
  const s = String(value);
  // Pure YYYY-MM-DD → interpret as LOCAL midnight so `.getDate()` matches the user's day.
  if (s.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(s);
}

// Re-exported for callers that want named MONTHS list (kept symmetry with dateFormat.js).
export { MONTHS };
